/**
 * Builds the link that opens a person's chat/profile on the platforms Yhuu
 * can't auto-send to (Snapchat, Instagram, Telegram, TikTok) and, for SMS, the
 * phone's Messages app addressed to the number. The Admin panel
 * uses it so the operator doesn't have to search for the username by hand.
 *
 * The link only OPENS the right place — the message still has to be pasted and
 * sent. On a phone with the app installed these links normally open the app.
 *
 * Returns null when there is nothing safe to link to (unknown platform, empty
 * or odd-looking username) so the caller can simply hide the button.
 */

export type ChatPlatform = 'Snapchat' | 'Instagram' | 'Telegram' | 'TikTok' | 'SMS';

// Usernames on these four platforms only use letters, digits, dot, underscore
// and dash. Anything else (spaces, emoji, a stray sentence) gets no link.
const HANDLE = /^[A-Za-z0-9._-]{1,64}$/;
const TELEGRAM_HANDLE = /^[A-Za-z][A-Za-z0-9_]{4,31}$/;

/**
 * Gets the bare username out of whatever the sender typed:
 *   "@ama_k"                          -> "ama_k"
 *   "  ama_k "                        -> "ama_k"
 *   "https://www.tiktok.com/@ama_k"   -> "ama_k"
 *   "snapchat.com/add/ama_k/?x=1"     -> "ama_k"
 */
export function cleanHandle(raw?: string | null): string {
  let v = (raw ?? '').trim();
  if (!v) return '';
  v = v.replace(/[?#].*$/, '').replace(/\/+$/, '');
  if (v.includes('/')) v = v.split('/').pop() || '';
  return v.replace(/^@+/, '').trim();
}

export function buildChatLink(
  platform: string | null | undefined,
  contact: string | null | undefined,
): string | null {
  const raw = (contact ?? '').trim();

  // SMS: an sms: link opens the phone's Messages app with this number filled in.
  // Local (0…) and international (+233…) formats both work — the phone knows
  // its own country — so we only tidy the spacing.
  if (platform === 'SMS') {
    const num = raw.replace(/[\s().-]/g, '');
    return /^\+?\d{7,15}$/.test(num) ? `sms:${num}` : null;
  }

  // Telegram also accepts a phone number, and people do type one there. It has
  // to be international format (no leading 0), otherwise the link goes nowhere.
  if (platform === 'Telegram') {
    const digits = raw.replace(/[\s()-]/g, '');
    if (/^\+?[1-9]\d{6,14}$/.test(digits)) return `https://t.me/+${digits.replace(/^\+/, '')}`;
  }

  const handle = cleanHandle(raw);
  if (!HANDLE.test(handle)) return null;
  // Telegram usernames are 5-32 characters, start with a letter, and only use
  // letters, digits and underscores.
  if (platform === 'Telegram' && !TELEGRAM_HANDLE.test(handle)) return null;

  switch (platform) {
    case 'Snapchat':  return `https://www.snapchat.com/add/${handle}`;
    case 'Instagram': return `https://ig.me/m/${handle}`;
    case 'Telegram':  return `https://t.me/${handle}`;
    case 'TikTok':    return `https://www.tiktok.com/@${handle}`;
    default:          return null;
  }
}
